import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Box,
  DrawerFooter,
  Button,
  VStack,
  Image,
  HStack,
  Radio,
  Stack,
  Text,
  IconButton,
  Portal,
  useToast,
  Center,
} from '@chakra-ui/react';
import { Api } from 'api';
import { LoadingText } from 'components';
import {
  createCustomerSocket,
  FormControlFormInput,
  FormControlFormRadioGroup,
  getExpertiseBackroundImageUrl,
} from 'core';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  BodyPartAnswerInfo,
  BodyPartExpertiseInfo,
  ExpertiseBodyDamagePartType,
  ExpertiseCarBodyTypes,
  ExpertiseTemplate,
  VehicleQueryCacheKeys,
} from 'types';
import { Socket } from 'socket.io-client';
import { Trans } from '@lingui/macro';
import { ExpertiseBodyworkExpertiseMicronSelector } from './ExpertiseBodyworkExpertiseMicronSelector';

export interface ExpertiseBodyworkMicronRecorderProps {
  isOpen: boolean;
  onClose: () => void;
  carBodyType: ExpertiseCarBodyTypes;
  customerId: number;
  vehicleTypeId: number;
  isDisabled?: boolean;
}

type MicronQuestionData = {
  [key: number]: string;
};

type MicronPartData = {
  [key: number]: number;
};

type ExpertiseSaveData = { microns: MicronPartData; answers: MicronQuestionData };
type PositionLookupTableRow = {
  left?: string;
  bottom?: string;
  top?: string;
  right?: string;
};

type PositionLookupTable = {
  [key: number]: PositionLookupTableRow;
};

const mikronPositions: PositionLookupTable = {
  // Sol taraf
  3: { bottom: '83%', left: '17%' },
  4: { bottom: '70%', left: '17%' },
  6: { bottom: '55%', left: '27%' },
  5: { bottom: '62%', left: '10%' },
  8: { bottom: '48%', left: '15%' },
  9: { bottom: '38%', left: '10%' },
  10: { bottom: '25%', left: '6%' },
  11: { bottom: '26%', left: '17%' },
  13: { bottom: '15%', left: '27%' },
  14: { bottom: '8%', left: '20%' },

  // Orta
  15: { right: '54%', bottom: '6%' },
  18: { right: '38%', bottom: '6%' },
  31: { right: '30%', top: '15%' },
  32: { right: '38%', top: '2%' },
  1: { right: '54%', top: '2%' },
  2: { right: '60%', top: '15%' },
  7: { right: '54%', top: '42%' },
  12: { left: '37%', bottom: '26%' },
  23: { left: '54%', bottom: '26%' },
  24: { right: '37%', top: '42%' },
  17: { bottom: '-3%', right: '35%' },
  16: { bottom: '-3%', right: '56%' },

  // Sağ taraf
  20: { bottom: '7%', right: '20%' },
  19: { top: '75%', right: '27%' },
  22: { bottom: '22%', right: '17%' },
  21: { bottom: '27%', right: '8%' },
  26: { bottom: '53%', right: '8%' },
  27: { bottom: '35%', right: '8%' },
  28: { bottom: '62%', right: '6%' },
  25: { top: '35%', right: '27%' },
  29: { bottom: '68%', right: '17%' },
  30: { bottom: '83%', right: '18%' },
};
const MicronInputPositionTables: {
  [key in ExpertiseCarBodyTypes]: PositionLookupTable;
} = {
  [ExpertiseCarBodyTypes.Sedan]: {
    ...mikronPositions,
    26: {
      bottom: '48%',
      right: '16%',
    },
    27: {
      bottom: '39%',
      right: '8%',
    },
  },
  [ExpertiseCarBodyTypes.Hatchback5]: {
    ...mikronPositions,
    26: {
      bottom: '48%',
      right: '16%',
    },
    27: {
      bottom: '39%',
      right: '8%',
    },
  },
  [ExpertiseCarBodyTypes.Hatchback2]: {
    ...mikronPositions,
    8: {
      bottom: '55%',
      left: '27%',
    },
    9: {
      bottom: '50%',
      left: '37%',
    },
    10: {
      bottom: '37%',
      left: '6%',
    },

    15: {
      bottom: '-3%',
      left: '35%',
    },
    18: {
      bottom: '6%',
      left: '38%',
    },
    19: {
      bottom: '6%',
      right: '38%',
    },
    20: {
      bottom: '-3%',
      right: '34%',
    },
    21: {
      top: '83%',
      right: '18%',
    },
    22: {
      top: '75%',
      right: '27%',
    },
    24: {
      top: '65%',
      right: '16%',
    },
    25: {
      top: '54%',
      right: '7%',
    },
    26: {
      right: '37%',
      top: '41%',
    },
    27: {
      top: '35%',
      right: '27%',
    },
  },
  [ExpertiseCarBodyTypes.Pickup]: {
    ...mikronPositions,
    26: {
      bottom: '47%',
      right: '15%',
    },
    27: {
      top: '53%',
      right: '8%',
    },
  },
  [ExpertiseCarBodyTypes.Pickup2]: {
    ...mikronPositions,
    8: {
      top: '36%',
      left: '27%',
    },
    9: {
      top: '42%',
      left: '37%',
    },
    10: {
      top: '57%',
      left: '37%',
    },
    11: {
      top: '50%',
      left: '19%',
    },
    12: {
      top: '42%',
      left: '14%',
    },
    13: {
      top: '50%',
      left: '9%',
    },
    14: {
      top: '62%',
      left: '7%',
    },
    15: {
      bottom: '22%',
      left: '18%',
    },
    18: {
      bottom: '3%',
      left: '20%',
    },
    19: {
      left: '34%',
      bottom: '-2%',
    },
    20: {
      right: '34%',
      bottom: '-2%',
    },
    21: {
      right: '20%',
      bottom: '4%',
    },
    22: {
      right: '19%',
      top: '72%',
    },
    23: {
      right: '9%',
      top: '62%',
    },
    24: {
      right: '8%',
      top: '49%',
    },
    25: {
      right: '16%',
      top: '43%',
    },
    26: {
      right: '24%',
      top: '49%',
    },
    27: {
      right: '35%',
      top: '57%',
    },
    28: {
      right: '35%',
      top: '42%',
    },
    29: {
      right: '7%',
      top: '29%',
    },
    30: {
      right: '14%',
      top: '20%',
    },
    31: {
      top: '23%',
      right: '18%',
    },
    32: {
      right: '15%',
      top: '10%',
    },
    33: {
      top: '14%',
      right: '31%',
    },
    34: {
      top: '2%',
      right: '37%',
    },
  },
  [ExpertiseCarBodyTypes.Minibus]: {
    ...mikronPositions,
    26: {
      top: '43%',
      right: '15%',
    },
    27: {
      top: '53%',
      right: '8%',
    },
  },
  [ExpertiseCarBodyTypes.Panelvan]: {
    ...mikronPositions,
    26: {
      top: '43%',
      right: '15%',
    },
    27: {
      top: '53%',
      right: '8%',
    },
  },
  [ExpertiseCarBodyTypes.CanliPanelvan]: {
    ...mikronPositions,
    7: {
      top: '42%',
      left: '37%',
    },
    26: {
      top: '43%',
      right: '15%',
    },
    27: {
      top: '53%',
      right: '8%',
    },
  },
  [ExpertiseCarBodyTypes.TekSurguPanelvan]: {
    ...mikronPositions,
    10: {
      top: '81%',
      left: '8%',
    },
    11: {
      top: '67%',
      left: '20%',
    },
    12: {
      bottom: '29%',
      left: '31%',
    },
    13: {
      top: '68%',
      left: '38%',
    },
    14: {
      top: '80%',
      left: '30%',
    },
    19: {
      bottom: '26%',
      right: '37%',
    },
    20: {
      top: '75%',
      right: '27%',
    },
    21: {
      top: '78%',
      right: '9%',
    },
    23: {
      top: '53%',
      right: '8%',
    },
    24: {
      top: '43%',
      right: '15%',
    },
    25: {
      top: '43%',
      right: '37%',
    },
    26: {
      top: '35%',
      right: '27%',
    },
    27: {
      top: '30%',
      right: '8%',
    },
    28: {
      top: '22%',
      right: '17%',
    },
    29: {
      top: '10%',
      right: '15%',
    },
    30: {
      top: '15%',
      right: '30%',
    },
    31: {
      top: '2%',
      right: '36%',
    },
  },
  [ExpertiseCarBodyTypes.SUV]: {
    ...mikronPositions,
    26: {
      bottom: '48%',
      right: '16%',
    },
    27: {
      bottom: '39%',
      right: '10%',
    },
  },
  [ExpertiseCarBodyTypes.Station]: {
    ...mikronPositions,

    26: {
      right: '8%',
      bottom: '53%',
    },
    27: {
      right: '8%',
      bottom: '35%',
    },
  },
};

const canGoToNextMicronPosition = (expertiseTemplates: ExpertiseTemplate[], micronPartNo: number): boolean => {
  return micronPartNo < expertiseTemplates.length;
};

const canGoToPrevMicronPosition = (micronPartNo: number): boolean => {
  return micronPartNo > 1;
};

export const ExpertiseBodyworkMicronRecorder: React.FC<ExpertiseBodyworkMicronRecorderProps> = ({
  isOpen,
  onClose,
  carBodyType,
  customerId,
  vehicleTypeId,
  isDisabled,
}) => {
  const toast = useToast();
  const micronValueInputRef = useRef<HTMLInputElement>(null);
  const inputPositionTable = useMemo(() => MicronInputPositionTables[carBodyType], [carBodyType]);
  const [focusedMikronPart, setFocusedMikronPart] = useState<number | null>(null);
  const expertiseImageUrl = getExpertiseBackroundImageUrl(carBodyType);
  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);

  const {
    data: expertises,
    isLoading: isExpertisesLoading,
    refetch,
  } = useQuery(
    VehicleQueryCacheKeys.GetBodyWorkMicrons,
    async () => {
      const { data: expertiseResponse } = await Api.Expertise.getCustomerBodyPartsExpertise(
        customerId,
        ExpertiseBodyDamagePartType.Micron,
      );
      const { expertiseBodyDamages, expertiseBodyAnswers } = expertiseResponse;
      return {
        microns: expertiseBodyDamages.reduce((accum: any, nextValue: BodyPartExpertiseInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyDamagePartId] = nextValue.expertiseBodyDamageValue;
          return accum;
        }, {} as MicronPartData),
        answers: expertiseBodyAnswers.reduce((accum: any, nextValue: BodyPartAnswerInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyQuestionId] = nextValue.answer;
          return accum;
        }, {} as MicronPartData),
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled: isOpen,
      cacheTime: 0,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Boyalı Değişen verileri çekilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const { data: expertiseTemplates, isLoading: isExpertisesTemplateLoading } = useQuery(
    VehicleQueryCacheKeys.GetBodyWorkMicronTemplates,
    async () => {
      const response = await Api.Expertise.getExpertiseTemplate(vehicleTypeId, ExpertiseBodyDamagePartType.Micron);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isOpen,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Boyalı değişen işlemleri için gereken verileri çekerken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const { values, setFieldValue, handleSubmit } = useFormik({
    enableReinitialize: true,
    initialValues: {
      answers: (expertises?.answers || {}) as MicronQuestionData,
      microns: (expertises?.microns || {}) as MicronPartData,
    },
    onSubmit: (data) => {
      if (expertiseTemplates) {
        const filledAnswers = expertiseTemplates.expertiseBodyQuestions.reduce((accum, next) => {
          // eslint-disable-next-line no-param-reassign
          accum[next.id] = data.answers[next.id] || '0';
          return accum;
        }, {} as any);
        // eslint-disable-next-line no-use-before-define
        saveExpertiseMutate.mutate({ answers: filledAnswers, microns: data.microns });
      }
    },
  });

  const saveExpertiseMutate = useMutation<void, any, ExpertiseSaveData>(
    async (data: ExpertiseSaveData) => {
      await Api.Expertise.saveExpertiseBodyParts({
        customerId,
        expertiseBodyDamagePartTypeId: ExpertiseBodyDamagePartType.Micron,
        bodyDamageAnswers: Object.entries(data.answers).map(([key, value]) => {
          return {
            answer: value,
            expertiseBodyQuestionId: parseInt(key, 10),
          };
        }),
        bodyDamageParts: Object.entries(data.microns).map(([key, value]) => {
          return {
            bodyDamageValue: value.toString(),
            expertiseBodyDamagePartId: parseInt(key, 10),
          };
        }),
      });
    },
    {
      onSuccess: () => {
        onClose();
        toast({
          title: 'Başarı ile kayıt edildi',
          status: 'success',
          isClosable: true,
        });
      },
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Kayıt sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const goToNextMicronPosition = () => {
    if (focusedMikronPart) {
      if (canGoToNextMicronPosition(expertiseTemplates?.expertiseBodyDamageParts || [], focusedMikronPart)) {
        if (micronValueInputRef.current) {
          const micronTempalte = expertiseTemplates?.expertiseBodyDamageParts[focusedMikronPart];
          if (micronTempalte) {
            const micron = values.microns[micronTempalte.id];
            micronValueInputRef.current.value = micron ? micron.toString() : '';
          }
        }
        setFocusedMikronPart(focusedMikronPart + 1);
        micronValueInputRef.current?.focus();
      }
    }
  };

  const gotToPrevMicronPosition = () => {
    if (focusedMikronPart && expertiseTemplates?.expertiseBodyDamageParts) {
      if (canGoToPrevMicronPosition(focusedMikronPart)) {
        if (micronValueInputRef.current) {
          const micronTempalte = expertiseTemplates?.expertiseBodyDamageParts[focusedMikronPart - 2];
          if (micronTempalte) {
            const micron = values.microns[micronTempalte.id];
            micronValueInputRef.current.value = micron ? micron.toString() : '';
          }
        }
        setFocusedMikronPart(focusedMikronPart - 1);
        micronValueInputRef.current?.focus();
      }
    }
  };

  useEffect(() => {
    if (customerId && isOpen) {
      const newCustomerSocket = createCustomerSocket(customerId.toString());
      setCustomerSocket(newCustomerSocket);

      const handleExpertiseBodyDamageChanged = (data: any) => {
        if (data) {
          refetch(); // Refetch expertises data when the socket event is triggered
        }
      };

      newCustomerSocket.on('expertiseBodyDamageChanged', handleExpertiseBodyDamageChanged);

      return () => {
        newCustomerSocket.off('expertiseBodyDamageChanged', handleExpertiseBodyDamageChanged);
        newCustomerSocket.disconnect();
      };
    }
    return () => {
      if (customerSocket) {
        customerSocket.disconnect();
      }
    };
  }, [customerId, isOpen, refetch]);

  const showLoading = !expertiseTemplates || isExpertisesTemplateLoading || isExpertisesLoading || !isOpen;

  return (
    <Portal>
      <Drawer isOpen={isOpen} onClose={onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent overflow="auto">
          <form noValidate onSubmit={handleSubmit}>
            <DrawerCloseButton />
            <DrawerHeader>
              <Trans>Mikron Kaydı Al</Trans>
            </DrawerHeader>

            <DrawerBody>
              {showLoading ? (
                <Center>
                  <LoadingText />
                </Center>
              ) : (
                <VStack gap={24} align="stretch">
                  <VStack>
                    <Box width="100%" position="relative" maxWidth="450px">
                      <Image src={expertiseImageUrl} fit="fill" width="100%" />
                      <Box position="absolute" top="0" left="0" right="0" bottom="0">
                        {expertiseTemplates.expertiseBodyDamageParts.map((expertiseTemplate, index) => {
                          const mikronPart = inputPositionTable[expertiseTemplate.displayId];
                          if (!mikronPart) {
                            return null;
                          }
                          return (
                            <ExpertiseBodyworkExpertiseMicronSelector
                              key={`mp${expertiseTemplate.displayId}`}
                              left={mikronPart.left}
                              bottom={mikronPart.bottom}
                              top={mikronPart.top}
                              right={mikronPart.right}
                              partNo={expertiseTemplate.name}
                              isFocused={focusedMikronPart === index + 1}
                              onClick={() => setFocusedMikronPart(index + 1)}
                              value={values.microns[expertiseTemplate.id]}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </VStack>
                  <Stack direction={['column', 'row-reverse']} width="100%" justify="center" spacing="24px">
                    {!isDisabled && (
                      <VStack>
                        {focusedMikronPart ? (
                          <>
                            <FormControlFormInput
                              label={`Mikron Değeri(Parça No ${
                                expertiseTemplates.expertiseBodyDamageParts[focusedMikronPart - 1].name
                              })`}
                              onChange={(event) =>
                                setFieldValue(
                                  `microns.${expertiseTemplates.expertiseBodyDamageParts[focusedMikronPart - 1].id}`,
                                  event.target.value,
                                )
                              }
                              ref={micronValueInputRef}
                              type="number"
                            />
                            <HStack justify="space-between" width="100%">
                              <IconButton
                                aria-label=""
                                icon={<ArrowLeftIcon />}
                                onClick={() => gotToPrevMicronPosition()}
                                disabled={!canGoToPrevMicronPosition(focusedMikronPart)}
                              />
                              <IconButton
                                aria-label=""
                                icon={<ArrowRightIcon />}
                                onClick={() => goToNextMicronPosition()}
                                disabled={
                                  !canGoToNextMicronPosition(
                                    expertiseTemplates.expertiseBodyDamageParts,
                                    focusedMikronPart,
                                  )
                                }
                              />
                            </HStack>
                          </>
                        ) : (
                          <Text>
                            <Trans>Mikron Değeri Girmek için Lütfen Üst Taraftan Parça Seçin</Trans>
                          </Text>
                        )}
                      </VStack>
                    )}

                    <Box>
                      {expertiseTemplates.expertiseBodyQuestions.map((expertiseBodyQuestion) => (
                        <FormControlFormRadioGroup
                          key={`bweq${expertiseBodyQuestion.id}`}
                          label={expertiseBodyQuestion.question}
                          onChange={(value) => {
                            setFieldValue(`answers.${expertiseBodyQuestion.id}`, value);
                          }}
                          value={values.answers[expertiseBodyQuestion.id] || '0'}
                          isDisabled={isDisabled}
                        >
                          <HStack spacing="24px">
                            <Radio value="1">
                              <Trans>Evet</Trans>
                            </Radio>
                            <Radio value="0">
                              <Trans>Hayır</Trans>
                            </Radio>
                          </HStack>
                        </FormControlFormRadioGroup>
                      ))}
                    </Box>
                  </Stack>
                </VStack>
              )}
            </DrawerBody>

            <DrawerFooter>
              <Button variant="outline" mr={3} onClick={onClose}>
                <Trans>İptal</Trans>
              </Button>
              {!isDisabled && (
                <Button type="submit">
                  <Trans>Kaydet</Trans>
                </Button>
              )}
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    </Portal>
  );
};
